import { useEffect, useRef, useState } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import { useFixedUIHidden } from '~/modules/root/hook/use-fixed-ui-hidden';
import { floatingDockOffset } from '~/modules/root/util/floating-dock';

/**
 * Scroll distance before the control appears. A full viewport is far enough
 * that the page top is genuinely out of reach, and short enough that it is
 * there when wanted on a long grid.
 */
const REVEAL_AFTER_VIEWPORTS = 1;

interface BackToTopProps {
    /**
     * Fill the control white instead of near-black. Named for the button
     * rather than the page: the dark editorial pages want it to stand out
     * against their ground, and the light product page wants it to match the
     * white chrome floating over the gallery — so it is not a page theme.
     */
    white?: boolean;
    /**
     * The page ground the control floats over is dark. Drives the keyboard
     * focus ring only — the control is fixed chrome outside any wrapper
     * carrying `focus-ring-on-dark`, so the ground has to arrive by prop.
     */
    overDark?: boolean;
}

/**
 * Floating "back to top" control for long scrolling pages, desktop only.
 *
 * Sits at the bottom right, stacked above the chat dock. The dock publishes
 * its own height as a CSS variable, so the two share the corner instead of
 * fighting for it — the button rides whatever is left below it.
 *
 * Always on. It was behind `back_to_top_button` while it and the control it
 * replaces coexisted; both are ungated now and the flag is unused.
 */
export const BackToTop = ({ white = false, overDark = false }: BackToTopProps) => {
    const [visible, setVisible] = useState(false);
    /**
     * A full-screen image viewer is up. Back to top scrolls the page behind
     * it, which the reader cannot see — so it stands down until the viewer
     * closes. Watched on the root element, since the viewer is mounted in a
     * different tree entirely.
     */
    const [viewerOpen, setViewerOpen] = useState(false);
    useEffect(() => {
        const root = document.documentElement;
        const read = () => setViewerOpen(root.dataset.imageViewer === 'open');
        read();
        const observer = new MutationObserver(read);
        observer.observe(root, {
            attributes: true,
            attributeFilter: ['data-image-viewer'],
        });
        return () => observer.disconnect();
    }, []);
    const isFixedUIHidden = useFixedUIHidden();
    const tracker = useTracker();
    const frameRef = useRef(0);

    useEffect(() => {
        const measure = () => {
            frameRef.current = 0;
            setVisible(
                window.scrollY > window.innerHeight * REVEAL_AFTER_VIEWPORTS,
            );
        };
        // Coalesce to one measurement per frame: both handlers read layout, and
        // a scroll fires far more often than the threshold can change.
        const schedule = () => {
            if (frameRef.current) return;
            frameRef.current = window.requestAnimationFrame(measure);
        };
        measure();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule, { passive: true });
        return () => {
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
        };
    }, []);

    const onClick = () => {
        window.scrollTo({
            top: 0,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)')
                .matches
                ? 'auto'
                : 'smooth',
        });
        tracker.log('BackToTopButtonClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
        });
    };

    return (
        <button
            type="button"
            onClick={onClick}
            aria-label="Back to top"
            // Hidden rather than unmounted so the fade runs both ways;
            // aria-hidden + inert keep it off the tab order while out.
            aria-hidden={!visible || viewerOpen}
            inert={!visible || viewerOpen}
            style={{ bottom: floatingDockOffset() }}
            className={cn(
                // Desktop only: the dock this clears never renders below md, so
                // the offset would collapse onto the mobile floating chrome.
                'max-md:hidden',
                // Bottom right, stacked above the chat dock rather than beside
                // it: `floatingDockOffset` is the shared measure of how tall
                // the messenger currently is, so the two never overlap whether
                // it is open, closed or hidden.
                'fixed right-8 z-fixed flex size-[2.625rem] items-center justify-center rounded-[1rem]',
                'transition-[opacity,transform] duration-300 ease-out',
                visible && !viewerOpen
                    ? 'translate-y-0 opacity-100'
                    : 'pointer-events-none -translate-y-2 opacity-0',
                // Matches its page rather than opposing it: white on the
                // light pages, near-black on the dark ones, so it reads as
                // part of the surface it floats over.
                white ? 'bg-white text-[#141414]' : 'bg-[#141414] text-white',
                // The keyboard ring draws just outside the fill, on the page
                // behind the control, so it follows the ground, not `white`.
                overDark && 'focus-ring-on-dark',
                isFixedUIHidden && 'hidden',
            )}
        >
            {/* Chevron rather than a full arrow: the smaller mark reads
                cleanly at this size and matches the pill-bar chevrons. */}
            <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                className="size-[1.1rem]"
            >
                <path
                    d="M6 14.5L12 8.5L18 14.5"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </button>
    );
};
